/**
 * Configuración compartida del cliente del camera-server.
 *
 * `CAMERA_HEADERS` añade `X-API-Key` cuando `VITE_CAMERA_TOKEN` está definido —
 * el camera-server (`tools/camera-server/server.py`) rechaza con 401 cualquier
 * POST cuyo header no coincida con `CAMERA_API_KEY` del backend.
 */
import { useCameraControl } from '../store/cameraControl';
import type { IntersectionDefinition } from '../types/intersection';
import type { Street } from '../data/streets';

export const CAMERA_URL = import.meta.env.DEV
  ? 'http://localhost:8310/camera/command'
  : '/camera/command';

const CAMERA_TOKEN = (import.meta.env.VITE_CAMERA_TOKEN as string) || '';

export const CAMERA_HEADERS: Record<string, string> = CAMERA_TOKEN
  ? { 'Content-Type': 'application/json', 'X-API-Key': CAMERA_TOKEN }
  : { 'Content-Type': 'application/json' };

/**
 * Vuela la cámara a un punto como acción explícita del operador (Modo
 * Edición: reposicionar/añadir). `source: 'manual'` hace que
 * CameraController.tsx aplique el fly_to aunque el toggle de encuadre
 * automático esté en manual — igual que el atajo de teclado "I".
 */
export function flyToPoint(center: [number, number], zoom = 18.5): void {
  fetch(CAMERA_URL, {
    method: 'POST',
    headers: CAMERA_HEADERS,
    body: JSON.stringify({
      type: 'fly_to',
      source: 'manual',
      center,
      zoom,
      pitch: 50,
      bearing: 0,
      duration: 2000,
    }),
  }).catch(() => {});
}

/** Buscador del header: ir a un regulador. Marca la cámara como manual para
 * que un auto-encuadre de alarma no la pise en los segundos siguientes. */
export function flyToRegulator(ix: IntersectionDefinition): void {
  flyToPoint(ix.center);
  useCameraControl.getState().markManual();
}

// Una calle no es un punto: por encima de ~400 m de extensión se encuadra
// más lejos para que quepa entera.
const LONG_STREET_DEG = 0.004;

export function flyToStreet(street: Street): void {
  const [w, s, e, n] = street.bbox;
  const extent = Math.max(e - w, n - s);
  flyToPoint(street.center, extent > LONG_STREET_DEG ? 16 : 17);
  useCameraControl.getState().markManual();
}
